<template>
  <div class="customer-dashboard">
    <div class="title-box">客户维度</div>
    <div class="row">
      <div class="col">
        <div class="card" v-loading="customerLoading">
          <h3>实时TOP5客户</h3>
          <div class="chart-container" ref="top5CustomerChart"></div>
          <div class="customer-detail" v-if="topCustomer">
            <h4>{{ topCustomer.customerName }}</h4>
            <p>铁路入库: {{ formatWeight(topCustomer.railwayInbound) }} 吨</p>
            <p>汽运入库: {{ formatWeight(topCustomer.roadInbound) }} 吨</p>
            <p>汽运出库: {{ formatWeight(topCustomer.roadOutbound) }} 吨</p>
          </div>
          <div v-else class="empty-text">暂无客户数据</div>
        </div>
      </div>

      <div class="col">
        <div class="card" v-loading="customerLoading">
          <h3>合同量及完成情况趋势</h3>
          <div class="chart-container" ref="contractTrendChart"></div>
          <div class="contract-stats">
            <div class="stat-item">
              <p>存煤到达</p>
              <p class="stat-value">{{ formatWeight(summary.storedCoal.arrival) }} 吨</p>
            </div>
            <div class="stat-item">
              <p>执行到达</p>
              <p class="stat-value">{{ formatWeight(summary.execution.arrival) }} 吨</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col">
        <div class="card" v-loading="stockTrendLoading">
          <h3>煤种库存趋势</h3>
          <div class="chart-container" ref="coalStockTrendChart"></div>
          <div class="filler-content">
            <div class="stock-alert">
              <div class="coal-legend">
                <div class="legend-item" v-for="item in inventoryLegend" :key="item.name">
                  <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                  <span>{{ item.name }} ({{ item.value }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  getStoreInventoryTrend,
  getTopCustomers
} from '../api'

export default {
  data() {
    return {
      customerLoading: false,
      stockTrendLoading: false,
      topCustomers: [],
      summary: {
        storedCoal: { arrival: 0, dispatch: 0 },
        execution: { arrival: 0, dispatch: 0 }
      },
      storeTrend: { trend: { months: [], outbondWeights: [], inbondWeights: [], stockWeight: [] } },
      top5Chart: null,
      contractChart: null,
      coalStockChart: null
    }
  },
  computed: {
    topCustomer() {
      return this.topCustomers[0]
    },
    inventoryLegend() {
      const stockWeight = this.storeTrend && this.storeTrend.stockWeight
      const inboundWeight = this.storeTrend && this.storeTrend.inboundWeight
      const outboundWeight = this.storeTrend && this.storeTrend.outboundWeight
      return [
        { name: '当前库存', color: '#4285f4', value: stockWeight || '0 万吨' },
        { name: '累计入库', color: '#34a853', value: inboundWeight || '0 万吨' },
        { name: '累计出库', color: '#f57c00', value: outboundWeight || '0 万吨' }
      ]
    }
  },
  mounted() {
    this.initCharts()
    this.loadTopCustomers()
    this.loadStoreTrend()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.top5Chart && this.top5Chart.dispose()
    this.contractChart && this.contractChart.dispose()
    this.coalStockChart && this.coalStockChart.dispose()
  },
  methods: {
    toNumber(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    },
    formatWeight(value) {
      return this.toNumber(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    loadTopCustomers() {
      this.customerLoading = true
      getTopCustomers({ dimension: 'month' })
        .then((data) => {
          this.topCustomers = data && Array.isArray(data.customerList) ? data.customerList.slice(0, 5) : []
          this.summary = data && data.summary ? data.summary : this.summary
          this.renderTopCustomerChart()
          this.renderContractChart()
        })
        .catch(() => {
          this.topCustomers = []
          this.renderTopCustomerChart()
          this.renderContractChart()
        })
        .finally(() => {
          this.customerLoading = false
        })
    },
    loadStoreTrend() {
      this.stockTrendLoading = true
      getStoreInventoryTrend()
        .then((data) => {
          this.storeTrend = data || { trend: { months: [], outbondWeights: [], inbondWeights: [], stockWeight: [] } }
          this.renderCoalStockChart()
        })
        .catch(() => {
          this.storeTrend = { trend: { months: [], outbondWeights: [], inbondWeights: [], stockWeight: [] } }
          this.renderCoalStockChart()
        })
        .finally(() => {
          this.stockTrendLoading = false
        })
    },
    initCharts() {
      this.top5Chart = echarts.init(this.$refs.top5CustomerChart)
      this.contractChart = echarts.init(this.$refs.contractTrendChart)
      this.coalStockChart = echarts.init(this.$refs.coalStockTrendChart)
      this.renderTopCustomerChart()
      this.renderContractChart()
      this.renderCoalStockChart()
    },
    renderTopCustomerChart() {
      if (!this.top5Chart) return
      const names = this.topCustomers.map((item) => item.customerName).reverse()
      const data = this.topCustomers.map((item) => {
        return this.toNumber(item.railwayInbound) + this.toNumber(item.roadInbound) + this.toNumber(item.roadOutbound)
      }).reverse()
      this.top5Chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '4%', right: '8%', bottom: '8%', top: '8%', containLabel: true },
        xAxis: { type: 'value' },
        yAxis: { type: 'category', data: names },
        series: [{ name: '合计', type: 'bar', data, itemStyle: { color: '#28a745' }, barWidth: 20 }]
      }, true)
    },
    renderContractChart() {
      if (!this.contractChart) return
      const stored = this.summary.storedCoal || {}
      const execution = this.summary.execution || {}
      this.contractChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['存煤', '执行'] },
        grid: { left: '4%', right: '8%', bottom: '8%', top: '14%', containLabel: true },
        xAxis: { type: 'category', data: ['到达', '发运'] },
        yAxis: { type: 'value' },
        series: [
          { name: '存煤', type: 'bar', data: [this.toNumber(stored.arrival), this.toNumber(stored.dispatch)], itemStyle: { color: '#007bff' } },
          { name: '执行', type: 'bar', data: [this.toNumber(execution.arrival), this.toNumber(execution.dispatch)], itemStyle: { color: '#17a2b8' } }
        ]
      }, true)
    },
    renderCoalStockChart() {
      if (!this.coalStockChart) return
      const trend = this.storeTrend.trend || {}
      this.coalStockChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['入库', '出库', '库存'], top: 0 },
        grid: { left: '4%', right: '8%', bottom: '8%', top: '14%', containLabel: true },
        xAxis: { type: 'category', data: trend.months || [] },
        yAxis: { type: 'value' },
        series: [
          { name: '入库', type: 'line', smooth: true, data: trend.inbondWeights || [], itemStyle: { color: '#34a853' } },
          { name: '出库', type: 'line', smooth: true, data: trend.outbondWeights || [], itemStyle: { color: '#f57c00' } },
          { name: '库存', type: 'line', smooth: true, data: trend.stockWeight || [], itemStyle: { color: '#4285f4' } }
        ]
      }, true)
    },
    handleResize() {
      this.top5Chart && this.top5Chart.resize()
      this.contractChart && this.contractChart.resize()
      this.coalStockChart && this.coalStockChart.resize()
    }
  }
}
</script>

<style scoped>
.customer-dashboard {
  padding: 0 20px 20px;
}
.title-box {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}
.row {
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: 20px;
}
.card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  min-height: 460px;
}
.card h3 {
  font-size: 16px;
  margin: 0 0 16px;
  color: #333;
}
.chart-container {
  height: 300px;
}
.customer-detail,
.contract-stats,
.filler-content {
  margin-top: 14px;
}
.customer-detail h4 {
  margin: 0 0 8px;
}
.customer-detail p {
  margin: 4px 0;
  color: #666;
}
.contract-stats {
  display: flex;
  gap: 12px;
}
.stat-item {
  flex: 1;
  background: #f8f9fa;
  border-radius: 4px;
  padding: 12px;
}
.stat-item p {
  margin: 0 0 6px;
}
.stat-value {
  font-size: 18px;
  color: #007bff;
  font-weight: 700;
}
.coal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.empty-text {
  color: #909399;
}
@media (max-width: 1100px) {
  .row {
    grid-template-columns: 1fr;
  }
}
</style>
